import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Paper,
  Button,
  useTheme,
  Typography,
  Tooltip,
  Switch,
  CircularProgress,
  Fab,
  IconButton,
  Card,
  Chip,
  Avatar,
  Grid,
} from "@mui/material";
import validateTasks from "../Validation/TaskValidation";
import TaskTable from "../Components/ActionManagerComponents/TaskTable";
import TaskList from "../Components/ActionManagerComponents/TaskList";
import { useParams, useNavigate } from "react-router-dom";
import {
  RecipeValidationProvider,
  useRecipeValidation,
} from "../Context/RecipeValidationContext";
import { gql, useQuery, useMutation } from "@apollo/client";
import ErrorPage from "./ErrorPage";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Preloader from "../Components/Preloader/Preloader";
import ChooseArgsDialog from "../Components/Dialogs/ChooseArgsDialog";
import BreadCrumb from "../Components/BreadCrumb";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Close,
  Description,
  Warning,
  Restaurant,
  Timer,
  Category,
  AssignmentTurnedIn,
  DragIndicator,
} from "@mui/icons-material";
import { getRecipeImage } from "../constants";

// Add this at the top of the file after imports
const mobileResponsiveStyles = {
  maxWidth: "100vw",
  overflowX: "hidden",
  boxSizing: "border-box",
};

// Add Recipe Summary component
const RecipeSummary = ({ recipeData }) => {
  const theme = useTheme();
  if (!recipeData) return null;

  // Convert seconds to minutes with proper rounding
  const convertToMinutes = (timeInSeconds) => {
    if (!timeInSeconds) return null;
    const minutes = Math.round(parseInt(timeInSeconds) / 60);
    return minutes > 0 ? minutes : null;
  };

  // Format times
  const prepTimeMinutes = convertToMinutes(recipeData.prep_time);
  const cookTimeMinutes = convertToMinutes(recipeData.cook_time);

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: 2,
        background:
          theme.palette.mode === "dark"
            ? "rgba(66, 66, 66, 0.8)"
            : "rgba(248, 249, 250, 0.8)",
        backdropFilter: "blur(8px)",
        borderLeft: `4px solid ${theme.palette.primary.main}`,
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3} md={2}>
          {recipeData.recipe_image ? (
            <Avatar
              src={getRecipeImage(recipeData.recipe_image)}
              alt={recipeData.recipe_name}
              sx={{
                width: 80,
                height: 80,
                border: `2px solid ${theme.palette.primary.main}`,
                boxShadow: theme.shadows[3],
              }}
            />
          ) : (
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: theme.palette.primary.main,
              }}
            >
              <Restaurant fontSize="large" />
            </Avatar>
          )}
        </Grid>

        <Grid item xs={12} sm={9} md={10}>
          <Typography variant="h5" color="primary" gutterBottom>
            {recipeData.recipe_name || "Unnamed Recipe"}
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1 }}>
            {recipeData.recipe_type && (
              <Chip
                icon={<Category fontSize="small" />}
                label={recipeData.recipe_type}
                size="small"
                color="secondary"
              />
            )}

            {recipeData.recipe_category && (
              <Chip
                label={recipeData.recipe_category}
                size="small"
                variant="outlined"
              />
            )}

            {prepTimeMinutes && (
              <Chip
                icon={<Timer fontSize="small" />}
                label={`Prep: ${prepTimeMinutes} min`}
                size="small"
              />
            )}

            {cookTimeMinutes && (
              <Chip
                icon={<Timer fontSize="small" />}
                label={`Cook: ${cookTimeMinutes} min`}
                size="small"
              />
            )}

            {recipeData.serving && (
              <Chip
                icon={<Restaurant fontSize="small" />}
                label={`Serves: ${recipeData.serving}`}
                size="small"
              />
            )}

            <Chip
              icon={<AssignmentTurnedIn fontSize="small" />}
              label="Managing Recipe Actions"
              size="small"
              color="primary"
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default function ActionManager() {
  const { recipe_id } = useParams();
  const [tasks, setTasks] = useState([]);
  const navigate = useNavigate();
  const [preloader, setPreloader] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPreloader(false);
    }, 1500); // 2-second delay

    // Clear timer on component unmount
    return () => clearTimeout(timer);
  }, []);
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  return (
    <>
      {preloader ? (
        <Preloader />
      ) : (
        <RecipeValidationProvider recipe_id={parseInt(recipe_id)}>
          <ActionManagerContent
            recipe_id={recipe_id}
            tasks={tasks}
            setTasks={setTasks}
            navigate={navigate}
            isMobile={isMobile}
          />
        </RecipeValidationProvider>
      )}
    </>
  );
}

// Add this component at the top level
const WarningMessage = ({ theme, onConfirm, onCancel }) => (
  <Card
    sx={{
      width: "600",
      // maxWidth: '90vw',
      borderLeft: `4px solid ${theme.palette.warning.main}`,
      boxShadow: theme.shadows[4],
      p: 2,
    }}
  >
    {/* Keep existing warning message UI */}
    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
      <Warning
        fontSize="large"
        sx={{ color: theme.palette.warning.main, mr: 2 }}
      />
      <Typography variant="h6" color="text.primary">
        Validation Issues Detected
      </Typography>
    </Box>

    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      <Box component="span" fontWeight={500}>
        Potential issues found:
      </Box>
      <ul
        style={{
          margin: "8px 0",
          paddingLeft: 24,
          color: theme.palette.text.secondary,
        }}
      >
        <li>Some tasks contain invalid parameters</li>
        <li>Required fields might be missing</li>
        <li>Cooking flow could be interrupted</li>
      </ul>
    </Typography>

    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        justifyContent: "flex-end",
        borderTop: `1px solid ${theme.palette.divider}`,
        pt: 2,
      }}
    >
      <Button
        variant="outlined"
        size="small"
        onClick={onCancel}
        sx={{ textTransform: "none", px: 3 }}
      >
        Review Tasks
      </Button>
      <Button
        variant="contained"
        size="small"
        color="warning"
        onClick={onConfirm}
        sx={{ textTransform: "none", px: 3 }}
      >
        Proceed Anyway
      </Button>
    </Box>
  </Card>
);

// Updated handlers

function ActionManagerContent({
  recipe_id,
  tasks,
  setTasks,
  navigate,
  isMobile,
}) {
  const theme = useTheme();
  const isValid = useRecipeValidation();
  const [showError, setShowError] = useState(false);
  const [isSaved, setIsSaved] = useState(true);
  const [recipeStatus, setRecipeStatus] = useState("");
  const [recipeVisibility, setRecipeVisibility] = useState("");
  const [saveRecipeActions, { loading: saving }] =
    useMutation(SAVE_RECIPE_MUTATION);
  const [modifyRecipeVisibility] = useMutation(
    MODIFY_RECIPE_VISIBLITY_MUTATION
  );
  const [open, setOpen] = useState(false);
  const [handleAddTask, setHandleAddTask] = useState({});
  const [recipeDescription, setRecipeDescription] = useState(null);
  const [showDescriptionPreview, setShowDescriptionPreview] = useState(true);
  const [taskHaveErrorConfirmation, setTaskHaveErrorConfirmation] =
    useState(false);
  const [activeId, setActiveId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);

  const floatingButtonStyles = {
    position: "fixed",
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    zIndex: 1000,
    [theme.breakpoints.down("sm")]: {
      right: theme.spacing(2),
      bottom: theme.spacing(2),
    },
  };

  const previewPanelStyles = {
    position: "fixed",
    right: showDescriptionPreview ? 0 : "-100%",
    top: 0,
    height: "100vh",
    width: isMobile ? "100%" : 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    transition: "right 0.3s ease-in-out",
    zIndex: 1200,
    p: 3,
    overflowY: "auto",
  };

  // Define sensors with improved settings for touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Reduce the drag activation distance to make it easier to start dragging
      activationConstraint: {
        distance: 3, // Reduced from 5 to be even more sensitive
        delay: 0, // No delay for immediate response
      },
    }),
    useSensor(TouchSensor, {
      // Make touch more responsive with very low delay and tolerance
      activationConstraint: {
        delay: 50, // Reduced from 100ms to 50ms for almost instant activation
        tolerance: 5, // Slightly reduced tolerance for better touch detection
      },
    })
  );

  // Status constants
  const STATUS = {
    PUBLISHED: "Publish",
    DRAFT: "Draft",
  };

  // Query and mutations
  const { data, loading, error, refetch } = useQuery(GET_RECIPE_ACTIONS_QUERY, {
    variables: { recipeId: recipe_id },
    skip: !recipe_id,
  });

  // Data initialization
  useEffect(() => {
    if (!loading && data?.getRecipeActions) {
      const recipeData = data.getRecipeActions;
      const recipeTasks = JSON.parse(recipeData.recipe_tasks || "[]");

      setRecipeVisibility(recipeData.recipe_visiblity);
      setRecipeStatus(recipeData.recipe_status);
      setRecipeDescription(recipeData.recipe_description || null);

      const savedTasks =
        recipeTasks.length > 0
          ? recipeTasks
          : JSON.parse(localStorage.getItem(`tasks_${recipe_id}`) || "[]");

      if (Array.isArray(savedTasks)) {
        localStorage.setItem(`tasks_${recipe_id}`, JSON.stringify(savedTasks));
        setTasks(validateTasks(savedTasks));
      }
    }
  }, [data, loading, recipe_id, setTasks]);

  // Error handling
  useEffect(() => {
    if (!loading && (error || !isValid?.isExist)) {
      const timer = setTimeout(() => setShowError(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [error, isValid, loading]);

  // Task management
  const validateAndSetTasks = (newTasks) => {
    try {
      setTasks(validateTasks(newTasks));
      setIsSaved(false);

      // Save to localStorage for persistence
      if (recipe_id && Array.isArray(newTasks)) {
        try {
          localStorage.setItem(`tasks_${recipe_id}`, JSON.stringify(newTasks));
        } catch (e) {
          console.error("Error saving to local storage:", e);
        }
      }
    } catch (error) {
      console.error("Task validation error:", error);
    }
  };

  // Enhanced add handler that supports position-based insertion
  const handleAddWithPosition = (task, insertPosition = -1) => {
    const taskId = Date.now() + Math.random(); // Generate unique ID
    const newTask = {
      ...task,
      id: taskId,
    };

    setTasks((prevTasks) => {
      let updatedTasks;
      if (insertPosition >= 0 && insertPosition <= prevTasks.length) {
        // Insert at specific position
        updatedTasks = [
          ...prevTasks.slice(0, insertPosition),
          newTask,
          ...prevTasks.slice(insertPosition),
        ];
      } else {
        // Append to end (default behavior)
        updatedTasks = [...prevTasks, newTask];
      }

      try {
        localStorage.setItem(
          `tasks_${recipe_id}`,
          JSON.stringify(updatedTasks)
        );
      } catch (e) {
        console.error("Error saving to local storage:", e);
      }

      // Show success toast
      toast.success(
        insertPosition >= 0
          ? "Task inserted successfully"
          : "Recipe action added",
        {
          autoClose: 1500,
          position: "bottom-center",
          hideProgressBar: true,
        }
      );

      return validateTasks(updatedTasks);
    });

    setIsSaved(false);

    // Success feedback
    if (window.navigator.vibrate) {
      window.navigator.vibrate(50);
    }
  };

  const handleCook = async () => {
    const performCook = async () => {
      if (isValid?.isAuthor) {
        await saveRecipeActions({
          variables: {
            recipeId: recipe_id,
            tasks: JSON.stringify(tasks),
            recipe_status: "",
          },
        });
      }
      navigate(`/cook-recipe/${recipe_id}`);
    };
    if (!isTasksValid() && !taskHaveErrorConfirmation) {
      toast.warn(
        <WarningMessage
          theme={theme}
          onConfirm={() => {
            toast.dismiss();
            setTaskHaveErrorConfirmation(true);
            performCook();
          }}
          onCancel={() => toast.dismiss()}
        />,
        { autoClose: false, closeOnClick: false }
      );
      return;
    }

    performCook();
  };
  const handleView = async () => {
    const performCook = async () => {
      if (isValid?.isAuthor) {
        await saveRecipeActions({
          variables: {
            recipeId: recipe_id,
            tasks: JSON.stringify(tasks),
            recipe_status: "",
          },
        });
      }
      navigate(`/recipe/${recipe_id}`);
    };

    if (!isTasksValid() && !taskHaveErrorConfirmation) {
      toast.warn(
        <WarningMessage
          theme={theme}
          onConfirm={() => {
            toast.dismiss();
            setTaskHaveErrorConfirmation(true);
            performCook();
          }}
          onCancel={() => toast.dismiss()}
        />,
        { autoClose: false, closeOnClick: false }
      );
      return;
    }

    performCook();
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);

    // Find the task being dragged for overlay display
    const draggedTask = tasks.find((task) => task.id === active.id);
    setDraggedItem(draggedTask);

    // Add haptic feedback for mobile devices
    if (window.navigator.vibrate) {
      window.navigator.vibrate(50); // Short vibration for drag start
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Clear active id
    setActiveId(null);
    setDraggedItem(null);

    // Provide success feedback
    if (over && active.id !== over.id) {
      if (window.navigator.vibrate) {
        window.navigator.vibrate([30, 30, 30]); // Pattern vibration for successful drop
      }

      setTasks((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        setIsSaved(false);

        // Show success toast for reordering
        toast.success("Task successfully reordered", {
          autoClose: 1500,
          position: "bottom-center",
          hideProgressBar: true,
        });

        return newItems;
      });
    }
  };

  const handleAdd = (task) => {
    handleAddWithPosition(task);
  };

  // Save handlers
  const handleSave = async (status) => {
    const performSave = async () => {
      try {
        await saveRecipeActions({
          variables: {
            recipeId: recipe_id,
            tasks: JSON.stringify(tasks),
            recipe_status: status,
          },
        });
        toast.success(
          `Recipe ${
            status === STATUS.DRAFT ? "saved" : "published"
          } successfully!`
        );
        setIsSaved(true);
        refetch();
      } catch (err) {
        toast.error(
          `Error ${status === STATUS.DRAFT ? "saving" : "publishing"} recipe!`
        );
      } finally {
        setTaskHaveErrorConfirmation(false);
      }
    };

    if (!isTasksValid() && !taskHaveErrorConfirmation) {
      toast.warn(
        <WarningMessage
          theme={theme}
          onConfirm={() => {
            toast.dismiss();
            setTaskHaveErrorConfirmation(true);
            performSave();
          }}
          onCancel={() => toast.dismiss()}
        />,
        { autoClose: false, closeOnClick: false }
      );
      return;
    }

    // Directly perform save if valid or confirmation given
    performSave();
  };

  const handleVisibilityChange = async (newVisibility) => {
    if (!isSaved) return;
    try {
      await modifyRecipeVisibility({
        variables: { recipeId: recipe_id, recipe_visiblity: newVisibility },
      });
      toast.success(`Visibility changed to ${newVisibility}!`);
      setIsSaved(true);
      refetch();
    } catch (err) {
      toast.error("Error changing visibility!");
    }
  };

  // Validation
  const isTasksValid = () =>
    tasks.every(
      (task) => task.valid && (task.type !== "ingredient" || task.args)
    );

  // Early returns
  if (!recipe_id) return <ErrorPage message="Broken link detected!" />;
  if (loading) return <Preloader />;
  if (showError) return <ErrorPage message="Recipe not found!" />;
  if (!isValid?.isAuthor)
    return <ErrorPage message="Unauthorized modification!" />;
  return (
    <>
      {recipeDescription && (
        <Box sx={previewPanelStyles}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 3,
              position: "sticky",
              top: 0,
              bgcolor: "background.paper",
              zIndex: 1,
              pt: 2,
            }}
          >
            <Typography variant="h6">Recipe Description</Typography>
            <IconButton
              onClick={() => setShowDescriptionPreview(false)}
              sx={{ ml: 2 }}
            >
              <Close />
            </IconButton>
          </Box>
          <Box
            dangerouslySetInnerHTML={{ __html: recipeDescription }}
            sx={{
              "& img": { maxWidth: "100%", height: "auto" },
              "& iframe": { maxWidth: "100%" },
              "& pre": {
                overflowX: "auto",
                bgcolor: "background.default",
                p: 2,
                borderRadius: 1,
              },
            }}
          />
        </Box>
      )}
      <BreadCrumb
        links={[{ label: "Dashboard", href: "/dashboard" }]}
        currentPage="Action Manager"
      />
      <ToastContainer />

      <Box sx={{ maxWidth: "100vw", overflowX: "hidden" }}>
        {/* Show recipe summary before the main content */}
        {data?.getRecipeActions && (
          <RecipeSummary recipeData={data.getRecipeActions} />
        )}

        {/* Help tips for non-technical users */}
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 3,
            mx: { xs: 1, sm: 3 },
            backgroundColor: theme.palette.background.paper,
            borderLeft: `4px solid ${theme.palette.info.main}`,
            display: "flex",
            alignItems: "center",
            borderRadius: 2,
            width: "auto",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            <b>Quick Help:</b>
            {isMobile ? (
              <span>
                {" "}
                Long-press and drag items to reorder. Tap a task to add it to
                your recipe.
              </span>
            ) : (
              <span>
                {" "}
                Click and drag items to reorder. Click a task to add it to your
                recipe.
              </span>
            )}
          </Typography>
        </Paper>

        <DndContext
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={tasks?.map((t) => t.id)}
            strategy={verticalListSortingStrategy}
          >
            <Box sx={containerStyles}>
              <Paper sx={tablePanelStyles} elevation={3}>
                <Box
                  sx={{
                    p: 3,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="h5" component="h1" color="primary">
                    Recipe Actions Builder
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Drag and drop tasks to create your recipe workflow
                  </Typography>
                </Box>
                <TaskTable
                  tasks={tasks}
                  setTasks={validateAndSetTasks}
                  isAuthor={isValid?.isAuthor}
                  onAdd={handleAdd}
                />
              </Paper>

              <Paper sx={listPanelStyles} elevation={3}>
                <Box
                  sx={{
                    p: 3,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="h6" component="h2" color="primary">
                    Recipe Preview
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Review and manage your recipe steps
                  </Typography>
                </Box>
                <TaskList
                  tasks={tasks}
                  setTasks={validateAndSetTasks}
                  isAuthor={isValid?.isAuthor}
                />

                <ActionButtons
                  recipeStatus={recipeStatus}
                  isTasksValid={() => true}
                  saving={saving}
                  onSave={handleSave}
                  onCook={handleCook}
                  onView={handleView}
                  onVisibilityChange={() =>
                    handleVisibilityChange(
                      recipeVisibility === "Private" ? "Public" : "Private"
                    )
                  }
                  recipeVisibility={recipeVisibility}
                  isSaved={isSaved}
                />
              </Paper>
            </Box>

            {/* Add drag overlay for better visual feedback */}
            <DragOverlay>
              {activeId && draggedItem ? (
                <Paper
                  elevation={6}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    width: "100%",
                    maxWidth: 400,
                    bgcolor: "primary.light",
                    color: "primary.contrastText",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    opacity: 0.9,
                    transform: "rotate(2deg)",
                    boxShadow:
                      "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
                  }}
                >
                  <DragIndicator />
                  <Typography variant="body1" fontWeight="medium">
                    {draggedItem.name || "Recipe Step"}
                  </Typography>
                </Paper>
              ) : null}
            </DragOverlay>

            <ChooseArgsDialog
              open={open}
              onClose={() => setOpen(false)}
              task={handleAddTask}
              onUpdate={(t) =>
                setTasks(
                  validateTasks(tasks?.map((s) => (s.id === t.id ? t : s)))
                )
              }
            />
          </SortableContext>
        </DndContext>
      </Box>
      {recipeDescription && (
        <Fab
          color="primary"
          sx={floatingButtonStyles}
          onClick={() => setShowDescriptionPreview(!showDescriptionPreview)}
        >
          {showDescriptionPreview ? <Close /> : <Description />}
        </Fab>
      )}
    </>
  );
}
// Styled components
const containerStyles = (theme) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  minHeight: "calc(100vh - 64px)",
  ...mobileResponsiveStyles,
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    gap: theme.spacing(1),
  },
});

const tablePanelStyles = (theme) => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.shape.borderRadius * 2,
  transition: "transform 0.2s, box-shadow 0.2s",
  width: "100%",
  "&:hover": {
    boxShadow: theme.shadows[6],
  },
  [theme.breakpoints.down("md")]: {
    order: 1,
  },
});

const listPanelStyles = (theme) => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.background.paper,
  transition: "transform 0.2s, box-shadow 0.2s",
  width: "100%",
  "&:hover": {
    boxShadow: theme.shadows[6],
  },
  [theme.breakpoints.down("md")]: {
    order: 2,
    my: 2,
  },
});

// Action buttons component
const ActionButtons = ({
  recipeStatus,
  isTasksValid = "true",
  saving,
  onSave,
  onCook,
  onView,
  onVisibilityChange,
  recipeVisibility,
  isSaved,
}) => {
  const theme = useTheme();
  if (!recipeStatus) {
    return (
      <Box
        sx={{
          mt: "auto",
          p: 3,
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.grey[900]
              : theme.palette.grey[100],
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <ButtonGroup>
            <ActionButton
              label="Save as Draft"
              onClick={() => onSave("Draft")}
              variant="outlined"
              disabled={!isTasksValid() || saving}
              loading={saving}
            />
            <ActionButton
              label="Cook Now"
              onClick={onCook}
              color="secondary"
              disabled={!isTasksValid()}
            />
          </ButtonGroup>
          {isSaved && (
            <VisibilitySection
              recipeVisibility={recipeVisibility}
              isTasksValid={isTasksValid}
              recipeStatus={recipeStatus}
              onSave={onSave}
            />
          )}
        </Box>
      </Box>
    );
  }
  if (recipeStatus === "Publish") {
    return (
      <Box
        sx={{
          mt: "auto",
          p: 3,
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.grey[900]
              : theme.palette.grey[100],
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <ButtonGroup>
            {/* <ActionButton
              label="Update as Draft"
              onClick={() => onSave("Draft")}
              variant="outlined"
              disabled={!isTasksValid() || saving}
              loading={saving}
            /> */}
            <ActionButton
              label="Save Changes"
              onClick={() => onSave("Publish")}
              disabled={!isTasksValid() || saving}
              loading={saving}
            />
            <ActionButton
              label="Cook Now"
              onClick={onCook}
              color="secondary"
              disabled={!isTasksValid()}
            />
            <ActionButton
              label="Open Recipe"
              onClick={onView}
              color="info"
              disabled={!isTasksValid()}
            />
          </ButtonGroup>
          {isSaved && (
            <VisibilitySection
              recipeVisibility={recipeVisibility}
              isTasksValid={isTasksValid}
              recipeStatus={recipeStatus}
              onSave={onSave}
            />
          )}
        </Box>
      </Box>
    );
  }
  if (recipeStatus === "Draft") {
    return (
      <Box
        sx={{
          mt: "auto",
          p: 3,
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.grey[900]
              : theme.palette.grey[100],
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <ButtonGroup>
            {/* <ActionButton
              label="Save & Publish"
              onClick={() => onSave("Publish")}
              disabled={!isTasksValid() || saving}
              loading={saving}
            /> */}
            <ActionButton
              label="Save Changes"
              onClick={() => onSave("Draft")}
              variant="outlined"
              disabled={!isTasksValid() || saving}
              loading={saving}
            />
            <ActionButton
              label="Cook Now"
              onClick={onCook}
              color="secondary"
              disabled={!isTasksValid()}
            />
            <ActionButton
              label="Open Recipe"
              onClick={onView}
              color="info"
              disabled={!isTasksValid()}
            />
          </ButtonGroup>
          {isSaved && (
            <VisibilitySection
              recipeVisibility={recipeVisibility}
              isTasksValid={isTasksValid}
              recipeStatus={recipeStatus}
              onSave={onSave}
            />
          )}
        </Box>
      </Box>
    );
  } else {
    return;
  }
};
// Sub-components
const ButtonGroup = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      gap: 2,
      justifyContent: "center",
      flexWrap: "wrap",
      width: "100%",
      py: 2,
    }}
  >
    {children}
  </Box>
);

const ActionButton = ({ label, loading, startIcon, ...props }) => (
  <Button
    {...props}
    variant="contained"
    size="medium"
    startIcon={!loading && startIcon}
    sx={{
      minWidth: 160,
      transition: "all 0.2s",
      "&:hover": {
        transform: "translateY(-1px)",
      },
    }}
  >
    {loading ? <CircularProgress size={24} /> : label}
  </Button>
);

const VisibilitySection = ({
  recipeVisibility,
  isTasksValid,
  recipeStatus,
  onSave,
}) => (
  <Stack spacing={2} alignItems="center" mt={2}>
    {recipeVisibility === "Private" && (
      <Typography variant="caption" color="textSecondary">
        Your recipe id under review, it will be publish after review process.
      </Typography>
    )}
    <VisibilityToggle
      visibility={recipeStatus}
      onToggle={onSave}
      disabled={!isTasksValid()}
    />
  </Stack>
);

const VisibilityToggle = ({ visibility, onToggle, disabled }) => {
  const isPublic = visibility === "Publish";
  const handleUpdate = () => onToggle(isPublic ? "Draft" : "Publish");

  return (
    <Tooltip title={disabled ? "Save changes first" : "Toggle visibility"}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Switch
          checked={isPublic}
          onChange={handleUpdate}
          disabled={disabled}
          color="primary"
          inputProps={{ "aria-label": "Toggle visibility" }}
        />
        <Typography variant="body2" color="text.secondary">
          {isPublic ? "Publish" : "Private"}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const buttonContainerStyles = {
  width: "100%",
  textAlign: "center",
  justifyContent: "center",
  p: 2,
  mt: "auto",
  gap: 1,
  display: "flex",
  flexDirection: "column",
  "@media (max-width: 500px)": { flexWrap: "wrap" },
};
ActionManagerContent.propTypes = {
  recipe_id: PropTypes.string.isRequired,
  tasks: PropTypes.array.isRequired,
  setTasks: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
};

const GET_RECIPE_ACTIONS_QUERY = gql`
  query GetRecipeActions($recipeId: ID!) {
    getRecipeActions(id: $recipeId) {
      id
      recipe_tasks
      recipe_status
      recipe_visiblity
      recipe_name
      recipe_description
      recipe_image
      recipe_type
      recipe_category
      prep_time
      cook_time
      serving
    }
  }
`;

const SAVE_RECIPE_MUTATION = gql`
  mutation SaveRecipeActions(
    $recipeId: ID!
    $tasks: String!
    $recipe_status: String
  ) {
    saveRecipeActions(
      recipeId: $recipeId
      tasks: $tasks
      recipe_status: $recipe_status
    ) {
      success
      message
    }
  }
`;
const MODIFY_RECIPE_VISIBLITY_MUTATION = gql`
  mutation ModifyRecipeVisiblity($recipeId: ID!, $recipe_visiblity: String) {
    modifyRecipeVisiblity(
      recipeId: $recipeId
      recipe_visiblity: $recipe_visiblity
    ) {
      success
      message
    }
  }
`;
